import { User } from '@auth0/auth0-react';

import { ApplicationConfig } from '@repo/sdk/models/components';

import { ATLAS_USER_IDENTIFIER_CLAIM } from '@/utils/auth-claims';

/**
 * Subset of Auth0 User properties used in AgentProxy mode.
 * Ensures compile-time verification of Auth0 compatibility.
 * Includes the atlasUserIdentifier custom claim that the UI expects.
 */
export type AgentProxyMockUser = Pick<User, 'email' | 'name' | 'sub' | 'email_verified'> & {
  [ATLAS_USER_IDENTIFIER_CLAIM]: string;
};

/**
 * ApplicationConfig with AgentProxy mode guaranteed to be present.
 * Used for type narrowing after isAgentProxyMode() check.
 */
export type AgentProxyApplicationConfig = ApplicationConfig & {
  preauthentication: NonNullable<ApplicationConfig['preauthentication']>;
};

/**
 * Type guard that narrows ApplicationConfig to AgentProxy mode.
 * After this check, TypeScript knows config.agentProxy exists and is defined.
 *
 * @example
 * if (isAgentProxyMode(config)) {
 *   // TypeScript knows config.agentProxy is defined
 *   config.agentProxy.mockUser.email; // No optional chaining needed
 * }
 */
export function isAgentProxyMode(config: ApplicationConfig): config is AgentProxyApplicationConfig {
  return config.preauthentication?.principal?.type === 'agent';
}

/**
 * Get mock user info for AgentProxy mode.
 * Returns an Auth0-compatible User object shape, or undefined if not in AgentProxy mode.
 */
export function getAgentProxyMockUser(config: ApplicationConfig): AgentProxyMockUser | undefined {
  if (!isAgentProxyMode(config)) {
    return undefined;
  }

  const { principal } = config.preauthentication;
  return {
    email: principal.email,
    name: principal.name,
    sub: principal.id,
    email_verified: true,
    // Custom claim used by useUser hook - use sub as the atlas user identifier
    [ATLAS_USER_IDENTIFIER_CLAIM]: principal.id,
  };
}
